<template>
  <v-form v-model="validFutureForm" ref="futureScheduleForm">
    <v-table class="compactTable moreCompact">
      <thead>
        <tr>
          <th
            id="th_inspType"
            class="align-center"
            :style="$vuetify?.display?.smAndDown ? 'width: 24% !important' : ''"
          >
            Inspection Type
          </th>
          <th id="th_7A57" class="align-center" :style="$vuetify?.display?.smAndDown ? 'width: 12% !important' : ''">
            IE.01<br />Required
          </th>
          <th id="th_7A58" class="align-center" :style="$vuetify?.display?.smAndDown ? 'width: 15% !important' : ''">
            IE.02<br />Last Date
          </th>
          <th id="th_7A59" class="align-center" :style="$vuetify?.display?.smAndDown ? 'width: 24% !important' : ''">
            IE.03<br />Interval
          </th>
          <th id="th_7A60" class="align-center" :style="$vuetify?.display?.smAndDown ? 'width: 25% !important' : ''">
            IE.04<br />Next Inspection Date
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, idx) in futureScheduleData" :key="idx">
          <td class="align-center">
            {{ configStore.getReferenceValue(REFERENCE_TABLE.SNBI_INSPECTION_TYPE, row.inspType) }}
          </td>
          <td class="align-center">
            <v-checkbox
              v-if="row.show7A57Checkbox"
              :id="'checkbox_snbi_required' + idx"
              :aria-describedby="'checkbox_snbi_required' + idx"
              :aria-label="'checkbox_snbi_required' + idx"
              class="noGridCheckbox justify-center"
              :isEditable="isEditable"
              true-value="1"
              false-value="0"
              v-model="row.is7A57RequiredValue"
              @update:model-value="updateFieldDependencies(row)"
              hide-details
              :disabled="!isEditable"
            >
            </v-checkbox>
            <span v-else>N/A</span>
          </td>
          <td class="align-center">
            <LabelDatefield
              textXl="12"
              textLg="12"
              textMd="12"
              textSm="12"
              :id="'txt_snbi_last_inspection_date_' + idx"
              v-model="row.lastInspectionDate"
            >
            </LabelDatefield>
          </td>
          <td :style="$vuetify?.display?.mdAndDown ? 'width: 24% !important' : 'width: 19% !important'">
            <div class="align-center" v-if="row.showInterval">
              <LabelText
                textXl="12"
                textLg="12"
                textMd="12"
                textSm="12"
                :id="'txt_snbi_inspection_interval_' + idx"
                :isEditable="isEditable"
                :disabled="true"
                v-model="row.inspectionInterval"
                appendWith=" mos"
                type="number"
                maxlength="2"
                textStyle="width:80%"
                allowNegativeInput
                :rules="
                  isEditable
                    ? [
                        (value) => {
                          return valueInRangeRule(value, 0, 99);
                        },
                      ]
                    : []
                "
                :isRequired="isEditable && row.is7A57RequiredValue == '1'"
                @update:modelValue="updateFieldDependencies(row); updateNextDateFields(row)"
              >
              </LabelText>
            </div>
            <div class="align-right" v-else-if="!row.showInterval && isEditable" style="width: 80%">
              <span class="pr-8">0</span>
              <span style="font-size: 1rem; float: right">mos</span>
            </div>
            <div v-else style="width: 92%" class="align-center">
              <span>0 mos</span>
            </div>
          </td>

          <td class="align-center">
            <LabelDatefield
              v-if="row.showInspDueDate"
              textXl="8"
              textLg="10"
              textMd="12"
              textSm="12"
              :id="'txt_snbi_next_inspection_date_' + idx"
              v-model="row.inspectionDueDate"
              :isEditable="isEditable"
              :isRequired="isEditable && row.is7A57RequiredValue == '1'"
              @update:modelValue="updateNextDateFields(row)"
              class="date_center"
            >
            </LabelDatefield>
            <span v-else>N/A</span>
          </td>
        </tr>
      </tbody></v-table
    >
  </v-form>
</template>
<script>
import { valueInRangeRule } from "@/composables/validationRules";
import LabelText from "@/components/common/LabelText.vue";
import LabelDatefield from "@/components/common/LabelDatefield.vue";
import { useConfigStore } from "@/stores/config";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { computed, ref, defineComponent } from "vue";
import { useInspectionStore } from "@/stores/inspection";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { useQuickSearchStore } from "@/stores/quickSearch";
import { useScheduleStore } from "@/components/inspection/store/schedule";
import { useUserPermissionStore } from "@/stores/userPermission";
import { INSPECTION_LINKS_SCHEDULE_PERMISSIONS } from "@/rbac";
import { ACCESS_LEVELS } from "@/rbac/accessLevels.js";
import { calculateNextInspType } from "@/composables/futureSchedule";
import { INSPECTION_STATUSES } from "@/constants/InspectionStatuses";
import { isValidDateString } from "@/composables/dateField";
import dayjs from "dayjs";

export default defineComponent({
  name: "FutureScheduleTable",
  emits: ["updateInterval", "updateNextDate", "saveLegacyScheduleData"],
  props: {
    moduleType: { default: MODULE_TYPES.INSPECTION },
  },
  setup(props, ctx) {
    const configStore = useConfigStore();
    const inspectionStore = useInspectionStore();
    const quickSearchStore = useQuickSearchStore();
    const scheduleStore = useScheduleStore();
    const userPermissionStore = useUserPermissionStore();
    const structureHeader = computed(() => quickSearchStore.getStructure());

    const hasAccessToEdit = computed({
      get() {
        if (structureHeader.value?.INSPECTIONSTATUS === INSPECTION_STATUSES.ACCEPTED) {
          return userPermissionStore.checkUserPermission(
            INSPECTION_LINKS_SCHEDULE_PERMISSIONS[ACCESS_LEVELS.CUSTOM.EDIT_APPROVED_INSPECTION],
            userPermissionStore.getUserPermissionByBrkeyFromCache(scheduleValue.value.BRKEY),
          );
        } else {
          return userPermissionStore.checkUserPermission(
            INSPECTION_LINKS_SCHEDULE_PERMISSIONS[ACCESS_LEVELS.EDIT],
            userPermissionStore.getUserPermissionByBrkeyFromCache(scheduleValue.value.BRKEY),
          );
        }
      },
    });

    const isEditable = computed(() => {
      if (props.moduleType == MODULE_TYPES.MANAGEMENT) {
        return !quickSearchStore.getErrorMessage() && scheduleValue.value?.BRKEY && hasAccessToEdit.value;
      } else {
        return inspectionStore.isEditable;
      }
    });
    let futureScheduleForm = ref(null);
    let validFutureForm = ref(false);
    let scheduleValue = ref({});

    const setScheduleValue = () => {
      scheduleValue.value = scheduleStore.getScheduleObject();
    };

    const resetScheduleValue = () => {
      scheduleValue.value = {};
    };

    const updateInspectionDueDate = (inspectionType, inspectionInterval) => {
      const row = futureScheduleData.value.find((i) => i.inspType === inspectionType);
      if (!inspectionInterval || row.is7A57RequiredValue === "0") {
        return;
      }
      const endDate = scheduleValue.value.SNBI_INSPECTION_TYPES.find((i) => i.inspType === inspectionType)?.endDate;
      if (isValidDateString(endDate)) {
        const date = dayjs(endDate);
        const newInspectionDueDate = date.add(+inspectionInterval, "month");
        row.inspectionDueDate = newInspectionDueDate;
      }
    };

    const futureScheduleData = ref([
      {
        inspType: "1",
        show7A57Checkbox: false,
        showInterval: false,
        showInspDueDate: false,
        is7A57RequiredValue: null,
        lastInspectionDate: computed({
          get() {
            return scheduleValue.value?.FT_PREVIOUS_DATE;
          },
          set(param) {
            scheduleValue.value.FT_PREVIOUS_DATE = param;
          },
        }),
        inspectionInterval: 0,
        inspectionDueDate: null,
      },
      {
        inspType: "2",
        show7A57Checkbox: true,
        showInterval: true,
        showInspDueDate: true,
        is7A57RequiredValue: computed({
          get() {
            return scheduleValue.value?.ROU_REQUIRED;
          },
          set(param) {
            scheduleValue.value.ROU_REQUIRED = param;
          },
        }),
        lastInspectionDate: computed({
          get() {
            return scheduleValue.value?.ROU_PREVIOUS_DATE;
          },
          set(param) {
            scheduleValue.value.ROU_PREVIOUS_DATE = param;
          },
        }),
        inspectionInterval: computed({
          get() {
            return scheduleValue.value?.ROU_INTERVAL;
          },
          set(param) {
            scheduleValue.value.ROU_INTERVAL = param;
            updateInspectionDueDate("2", param);
          },
        }),
        inspectionDueDate: computed({
          get() {
            return scheduleValue.value?.ROU_NEXT_DATE;
          },
          set(param) {
            scheduleValue.value.ROU_NEXT_DATE = param;
          },
        }),
      },
      {
        inspType: "3",
        show7A57Checkbox: true,
        showInterval: true,
        showInspDueDate: true,
        is7A57RequiredValue: computed({
          get() {
            return scheduleValue.value?.UW_REQUIRED;
          },
          set(param) {
            scheduleValue.value.UW_REQUIRED = param;
          },
        }),
        lastInspectionDate: computed({
          get() {
            return scheduleValue.value?.UW_PREVIOUS_DATE;
          },
          set(param) {
            scheduleValue.value.UW_PREVIOUS_DATE = param;
          },
        }),
        inspectionInterval: computed({
          get() {
            return scheduleValue.value?.UW_INTERVAL;
          },
          set(param) {
            scheduleValue.value.UW_INTERVAL = param;
            updateInspectionDueDate("3", param);
          },
        }),
        inspectionDueDate: computed({
          get() {
            return scheduleValue.value?.UW_NEXT_DATE;
          },
          set(param) {
            scheduleValue.value.UW_NEXT_DATE = param;
          },
        }),
      },
      {
        inspType: "4",
        show7A57Checkbox: true,
        showInterval: true,
        showInspDueDate: true,
        is7A57RequiredValue: computed({
          get() {
            return scheduleValue.value?.NSTM_REQUIRED;
          },
          set(param) {
            scheduleValue.value.NSTM_REQUIRED = param;
          },
        }),
        lastInspectionDate: computed({
          get() {
            return scheduleValue.value?.NSTM_PREVIOUS_DATE;
          },
          set(param) {
            scheduleValue.value.NSTM_PREVIOUS_DATE = param;
          },
        }),
        inspectionInterval: computed({
          get() {
            return scheduleValue.value?.NSTM_INTERVAL;
          },
          set(param) {
            scheduleValue.value.NSTM_INTERVAL = param;
            updateInspectionDueDate("4", param);
          },
        }),
        inspectionDueDate: computed({
          get() {
            return scheduleValue.value?.NSTM_NEXT_DATE;
          },
          set(param) {
            scheduleValue.value.NSTM_NEXT_DATE = param;
          },
        }),
      },
      {
        inspType: "5",
        show7A57Checkbox: false,
        showInterval: false,
        showInspDueDate: false,
        is7A57RequiredValue: null,
        lastInspectionDate: computed({
          get() {
            return scheduleValue.value?.DMG_PREVIOUS_DATE;
          },
          set(param) {
            scheduleValue.value.DMG_PREVIOUS_DATE = param;
          },
        }),
        inspectionInterval: 0,
        inspectionDueDate: null,
      },
      {
        inspType: "6",
        show7A57Checkbox: true,
        showInterval: true,
        showInspDueDate: true,
        is7A57RequiredValue: computed({
          get() {
            return scheduleValue.value?.IND_REQUIRED;
          },
          set(param) {
            scheduleValue.value.IND_REQUIRED = param;
          },
        }),
        lastInspectionDate: computed({
          get() {
            return scheduleValue.value?.IND_PREVIOUS_DATE;
          },
          set(param) {
            scheduleValue.value.IND_PREVIOUS_DATE = param;
          },
        }),
        inspectionInterval: computed({
          get() {
            return scheduleValue.value?.IND_INTERVAL;
          },
          set(param) {
            scheduleValue.value.IND_INTERVAL = param;
            updateInspectionDueDate("6", param);
          },
        }),
        inspectionDueDate: computed({
          get() {
            return scheduleValue.value?.IND_NEXT_DATE;
          },
          set(param) {
            scheduleValue.value.IND_NEXT_DATE = param;
          },
        }),
      },
      {
        inspType: "7",
        show7A57Checkbox: true,
        showInterval: true,
        showInspDueDate: true,
        is7A57RequiredValue: computed({
          get() {
            return scheduleValue.value?.OS_REQUIRED;
          },
          set(param) {
            scheduleValue.value.OS_REQUIRED = param;
          },
        }),
        lastInspectionDate: computed({
          get() {
            return scheduleValue.value?.OS_PREVIOUS_DATE;
          },
          set(param) {
            scheduleValue.value.OS_PREVIOUS_DATE = param;
          },
        }),
        inspectionInterval: computed({
          get() {
            return scheduleValue.value?.OS_INTERVAL;
          },
          set(param) {
            scheduleValue.value.OS_INTERVAL = param;
            updateInspectionDueDate("7", param);
          },
        }),
        inspectionDueDate: computed({
          get() {
            return scheduleValue.value?.OS_NEXT_DATE;
          },
          set(param) {
            scheduleValue.value.OS_NEXT_DATE = param;
          },
        }),
      },
      {
        inspType: "8",
        show7A57Checkbox: true,
        showInterval: true,
        showInspDueDate: true,
        is7A57RequiredValue: computed({
          get() {
            return scheduleValue.value?.SERV_REQUIRED;
          },
          set(param) {
            scheduleValue.value.SERV_REQUIRED = param;
          },
        }),
        lastInspectionDate: computed({
          get() {
            return scheduleValue.value?.SERV_PREVIOUS_DATE;
          },
          set(param) {
            scheduleValue.value.SERV_PREVIOUS_DATE = param;
          },
        }),
        inspectionInterval: computed({
          get() {
            return scheduleValue.value?.SERV_INTERVAL;
          },
          set(param) {
            scheduleValue.value.SERV_INTERVAL = param;
            updateInspectionDueDate("8", param);
          },
        }),
        inspectionDueDate: computed({
          get() {
            return scheduleValue.value?.SERV_NEXT_DATE;
          },
          set(param) {
            scheduleValue.value.SERV_NEXT_DATE = param;
          },
        }),
      },
      {
        inspType: "9",
        show7A57Checkbox: false,
        showInterval: false,
        showInspDueDate: false,
        is7A57RequiredValue: null,
        lastInspectionDate: computed({
          get() {
            return scheduleValue.value?.SCM_PREVIOUS_DATE;
          },
          set(param) {
            scheduleValue.value.SCM_PREVIOUS_DATE = param;
          },
        }),
        inspectionInterval: 0,
        inspectionDueDate: null,
      },
      {
        inspType: "E",
        show7A57Checkbox: true,
        showInterval: true,
        showInspDueDate: true,
        is7A57RequiredValue: computed({
          get() {
            return scheduleValue.value?.ELEM_REQUIRED;
          },
          set(param) {
            scheduleValue.value.ELEM_REQUIRED = param;
          },
        }),
        lastInspectionDate: computed({
          get() {
            return scheduleValue.value?.ELEM_PREVIOUS_DATE;
          },
          set(param) {
            scheduleValue.value.ELEM_PREVIOUS_DATE = param;
          },
        }),
        inspectionInterval: computed({
          get() {
            return scheduleValue.value?.ELEM_INTERVAL;
          },
          set(param) {
            scheduleValue.value.ELEM_INTERVAL = param;
            updateInspectionDueDate("E", param);
          },
        }),
        inspectionDueDate: computed({
          get() {
            return scheduleValue.value?.ELEM_NEXT_DATE;
          },
          set(param) {
            scheduleValue.value.ELEM_NEXT_DATE = param;
          },
        }),
      },
      {
        inspType: "P",
        show7A57Checkbox: false,
        showInterval: false,
        showInspDueDate: false,
        is7A57RequiredValue: null,
        lastInspectionDate: computed({
          get() {
            return scheduleValue.value?.PROB_PREVIOUS_DATE;
          },
          set(param) {
            scheduleValue.value.PROB_PREVIOUS_DATE = param;
          },
        }),
        inspectionInterval: 0,
        inspectionDueDate: null,
      },
      {
        inspType: "Q",
        show7A57Checkbox: false,
        showInterval: false,
        showInspDueDate: false,
        is7A57RequiredValue: null,
        lastInspectionDate: computed({
          get() {
            return scheduleValue.value?.QA_PREVIOUS_DATE;
          },
          set(param) {
            scheduleValue.value.QA_PREVIOUS_DATE = param;
          },
        }),
        inspectionInterval: 0,
        inspectionDueDate: null,
      },
    ]);

    const saveLegacyScheduleData = () => {
      ctx.emit("saveLegacyScheduleData", futureScheduleData.value);
    };

    const updateFieldDependencies = async (inspTypeDetail) => {
      await validateForm();
      switch (inspTypeDetail.inspType) {
        case "Z":
        case "1":
        case "2":
        case "5":
        case "6":
        case "8":
        case "9":
        case "P":
        case "Q":
        case "3":
        case "4":
        case "E":
        case "7":
          //update 7A59
          inspTypeDetail.inspectionInterval = scheduleStore.getInterval(inspTypeDetail);
          //update B.IE.05
          updateInspTypeInterval(inspTypeDetail);
          break;
      }
    };

    const updateInspTypeInterval = (inspTypeDetail) => {
      ctx.emit("updateInterval", inspTypeDetail);
    };
    const updateNextDateFields = (inspTypeDetail) => {
      setNextInspType();
      ctx.emit("updateNextDate", inspTypeDetail);
    };

    const validateForm = async () => {
      await futureScheduleForm.value?.validate();
      let validDates = true;
      for (const d of futureScheduleData.value ?? []) {
        if (d.is7A57RequiredValue && d.is7A57RequiredValue == "1" && !d.inspectionDueDate) {
          validDates = false;
        }
      }
      return validDates && validFutureForm.value;
    };

    const resetFormValidation = () => {
      futureScheduleForm.value?.resetValidation();
    };

    const setNextInspType = () => {
      let arrayForRecalc = [];
      for (const entry of futureScheduleData.value) {
        // only attempt if we have a valid inspection due date
        if (entry.inspectionDueDate) {
          arrayForRecalc.push({
            inspectionDueDate: entry.inspectionDueDate,
            inspType: entry.inspType,
          });
        }
      }
      scheduleValue.value.NEXT_INSP_TYPE = calculateNextInspType(arrayForRecalc);
    };

    return {
      resetScheduleValue,
      saveLegacyScheduleData,
      setScheduleValue,
      futureScheduleData,
      configStore,
      REFERENCE_TABLE,
      valueInRangeRule,
      isEditable,
      validFutureForm,
      updateFieldDependencies,
      validateForm,
      futureScheduleForm,
      updateNextDateFields,
      resetFormValidation,
      setNextInspType,
    };
  },
  components: { LabelText, LabelDatefield },
});
</script>
<style scoped lang="scss">
td.align-center :deep(.v-checkbox) {
  padding-left: 8px !important;
}
td.align-center :deep(.v-row) {
  justify-content: center;
}

.date_center :deep(.v-field__field input) {
  align-items: center !important;
  text-align: center !important;
}
</style>
